import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/use-toast';
import { apiService } from '@/services/api';
import { 
  Building2, 
  Mail, 
  CheckCircle, 
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const ClaimBusiness: React.FC = () => {
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    email: '',
    businessName: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await apiService.claimOperatorRequest(formData.email, formData.businessName);
      setMessageType('success');
      setMessage('We\'ve sent you a preview link! Check your email to review your listing.');
      
      // If we get a preview URL in the response, show it
      if (response.data?.preview_url) {
        setMessage(`We've sent you a preview link! You can also access it directly: ${response.data.preview_url}`);
      }
      
      setFormData({ email: '', businessName: '' });
    } catch (err: any) {
      setMessageType('error');
      setMessage(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Claim Your Business - SaunaDip</title>
        <meta name="description" content="Claim and manage your sauna business listing on SaunaDip" />
      </Helmet>

      <div className="min-h-screen bg-sauna-beige py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-sauna-navy mb-4">Claim Your Business</h1>
              <p className="text-xl text-gray-700">
                Has your business been discovered by SaunaDip? Enter your details below 
                and we'll send you a preview link to review your listing.
              </p>
            </div>

            {/* Claim Form Card */}
            <Card className="bg-white shadow-lg rounded-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Request Preview Link
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="businessName" className="text-sm font-medium text-gray-700">
                        Business Name *
                      </Label>
                      <Input
                        id="businessName"
                        type="text"
                        value={formData.businessName}
                        onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                        placeholder="Enter your business name"
                        required
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Enter your email address"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading} 
                    className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white py-3 text-lg rounded-lg shadow"
                  >
                    {loading ? (
                      <>
                        <Spinner className="w-4 h-4 mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Preview Link
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                {message && (
                  <Alert className={`mt-6 ${messageType === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'} rounded-lg`}>
                    {messageType === 'success' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    )}
                    <AlertDescription className={messageType === 'success' ? 'text-green-800' : 'text-red-800'}>
                      {message}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Information Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <Card className="bg-white shadow rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg">How It Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <p className="text-sm text-gray-600">Enter your business details</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <p className="text-sm text-gray-600">We'll send you a preview link</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <p className="text-sm text-gray-600">Review and approve your listing</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow rounded-xl">
                <CardHeader>
                  <CardTitle className="text-lg">Need Help?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Can't find your business or need assistance? Contact our support team:
                  </p>
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> support@saunadip.com</p>
                    <p><strong>Phone:</strong> +353 1 234 5678</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Benefits Section */}
            <Card className="bg-white shadow rounded-xl mt-8">
              <CardHeader>
                <CardTitle className="text-lg">Why Claim Your Business?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Verified Listing</h3>
                    <p className="text-sm text-gray-600">Get a verified badge and build trust with customers</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Complete Control</h3>
                    <p className="text-sm text-gray-600">Manage your business information and respond to reviews</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Mail className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Direct Bookings</h3>
                    <p className="text-sm text-gray-600">Receive booking inquiries directly from our platform</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClaimBusiness; 